import { useState, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";

// Aba interna Raz — Direcionador de Redação e Arte.
// Diretrizes curadas (raz_direcionador) + apontamentos ao vivo dos cards.

const AREA_INFO: Record<string, { label: string; color: string; bg: string }> = {
  redacao: { label: "Redação", color: "#1E40AF", bg: "#DBEAFE" },
  arte:    { label: "Arte",    color: "#BE185D", bg: "#FCE7F3" },
  ambos:   { label: "Redação + Arte", color: "#6B21A8", bg: "#F3E8FF" },
  produto: { label: "Produto", color: "#065F46", bg: "#D1FAE5" },
};

function mesLabel(periodo: string | null) {
  if (!periodo) return "";
  const [y, m] = periodo.split("-");
  const meses = ["", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  return `${meses[Number(m)] ?? periodo} ${y}`;
}

export default function Direcionador() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;
  const [aba, setAba] = useState<"diretrizes" | "apontamentos" | "validar">("diretrizes");
  const [filtroArea, setFiltroArea] = useState<"todas" | "redacao" | "arte" | "ambos" | "produto">("todas");

  // Validação de redação por doc/texto/link — com chunk, progresso e histórico.
  const CHUNK = 2; // posts por chamada
  const [valTexto, setValTexto] = useState("");
  const [valLink, setValLink] = useState("");
  const [valArquivo, setValArquivo] = useState("");
  const [valPosts, setValPosts] = useState<any[]>([]);
  const [valResumo, setValResumo] = useState("");
  const [valProg, setValProg] = useState<{ done: number; total: number } | null>(null);
  const [valId, setValId] = useState<number | null>(null);
  const [valEstado, setValEstado] = useState<"idle" | "running" | "paused" | "done">("idle");
  const ctrlRef = useRef<"run" | "pause" | "cancel">("run");
  const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

  const fetchDoc = trpc.direcionador.fetchDoc.useMutation();
  const valIniciar = trpc.direcionador.valIniciar.useMutation();
  const valChunk = trpc.direcionador.valChunk.useMutation();
  const valResolver = trpc.direcionador.valResolver.useMutation();
  const { data: valHistorico = [], refetch: refetchHist } = trpc.direcionador.valList.useQuery(
    { clienteId: clientId }, { enabled: aba === "validar" }
  );
  const utils = trpc.useUtils();
  const [exportando, setExportando] = useState(false);
  async function exportarPromptClaude() {
    setExportando(true);
    try {
      const r = await utils.direcionador.exportPrompt.fetch({ clienteId: clientId });
      if (!r?.texto) return;
      try { await navigator.clipboard.writeText(r.texto); } catch {}
      const blob = new Blob([r.texto], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `direcionador-prompt-${new Date().toISOString().slice(0, 10)}.md`;
      a.click(); URL.revokeObjectURL(url);
      alert(`Direcionador exportado (${r.total} itens): copiado pra área de transferência e baixado como .md. Cole no Claude e depois cole o conteúdo a validar.`);
    } finally { setExportando(false); }
  }

  function splitPosts(t: string): string[] {
    const parts = t.split(/\n(?=\**\s*POST\s+\d+\b)/i).map(s => s.trim()).filter(s => s.length > 10);
    return parts.length ? parts : [t.trim()];
  }
  async function buscarLink() {
    if (!valLink.trim()) return;
    const r = await fetchDoc.mutateAsync({ url: valLink.trim() });
    if (r.ok) { setValTexto(r.texto); setValArquivo("(do link)"); }
    else alert(r.erro);
  }
  function onUploadArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setValArquivo(f.name);
    const reader = new FileReader();
    reader.onload = () => setValTexto(String(reader.result ?? ""));
    reader.readAsText(f);
  }
  const validando = valEstado === "running" || valEstado === "paused";
  async function rodarValidacao() {
    const posts = splitPosts(valTexto);
    if (!posts.length) return;
    ctrlRef.current = "run"; setValEstado("running");
    const fonte = valArquivo || (valLink ? valLink : "colado");
    const { id } = await valIniciar.mutateAsync({ clienteId: clientId, fonte: fonte.slice(0, 500) });
    setValId(id); setValPosts([]); setValResumo(""); setValProg({ done: 0, total: posts.length });
    let cancelado = false;
    for (let i = 0; i < posts.length; i += CHUNK) {
      // Pausa: espera enquanto pausado; sai se cancelar.
      while (ctrlRef.current === "pause") { setValEstado("paused"); await sleep(250); }
      if (ctrlRef.current === "cancel") { cancelado = true; break; }
      setValEstado("running");
      const lote = posts.slice(i, i + CHUNK).join("\n\n");
      try {
        const r = await valChunk.mutateAsync({ id, clienteId: clientId, texto: lote });
        setValPosts(prev => [...prev, ...(r.posts ?? [])]);
        if (r.resumo) setValResumo(r.resumo);
      } catch { /* segue o baile mesmo se um lote falhar */ }
      setValProg({ done: Math.min(i + CHUNK, posts.length), total: posts.length });
    }
    setValEstado(cancelado ? "idle" : "done");
    refetchHist();
  }
  async function abrirEnvio(id: number) {
    const r = await utils.direcionador.valGet.fetch({ id });
    if (!r) return;
    setValId(r.id); setValPosts(r.posts ?? []); setValResumo(r.resumo ?? "");
    setValProg({ done: 1, total: 1 }); setValEstado("done");
  }
  function toggleItem(postId: number, issueId: number, resolvido: boolean) {
    setValPosts(prev => prev.map(p => p.id === postId
      ? { ...p, issues: p.issues.map((is: any) => is.id === issueId ? { ...is, resolvido } : is) }
      : p));
    if (valId != null) valResolver.mutate({ id: valId, postId, issueId, resolvido });
  }
  function baixarApontamentos() {
    const linhas: string[] = ["# Validação de Redação — Direcionador", ""];
    if (valResumo) linhas.push(`> ${valResumo}`, "");
    for (const p of valPosts) {
      const vmap: Record<string, string> = { aprovado: "✅ Aprovado", ajustes: "🟡 Ajustes", reprovado: "🔴 Reprovado" };
      linhas.push(`## ${p.titulo}  —  ${vmap[p.veredito] ?? p.veredito}`, "");
      if (!p.issues?.length) { linhas.push("_Sem apontamentos._", ""); continue; }
      for (const i of p.issues) {
        linhas.push(`- [${i.resolvido ? "x" : " "}] **[${i.categoria}/${i.severidade}]** ${i.problema}`);
        if (i.trecho) linhas.push(`  - Trecho: "${i.trecho}"`);
        if (i.sugestao) linhas.push(`  - Sugestão: ${i.sugestao}`);
      }
      linhas.push("");
    }
    const blob = new Blob([linhas.join("\n")], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `validacao-redacao-${new Date().toISOString().slice(0, 10)}.md`;
    a.click(); URL.revokeObjectURL(url);
  }

  const { data: diretrizes = [], isLoading } = trpc.direcionador.list.useQuery({ clienteId: clientId });
  const { data: apontamentos = [] } = trpc.direcionador.apontamentos.useQuery(
    { clienteId: clientId },
    { enabled: aba === "apontamentos" }
  );

  const filtradas = useMemo(
    () => diretrizes.filter(d => filtroArea === "todas" || d.area === filtroArea),
    [diretrizes, filtroArea]
  );
  const gerais = filtradas.filter(d => d.escopo === "geral");
  const geraisRedacao = gerais.filter(d => d.area === "redacao" || d.area === "ambos");
  const geraisArte = gerais.filter(d => d.area === "arte" || d.area === "ambos");
  // Produto = o que a plataforma faz, o que não faz e o que não pode ser prometido.
  const geraisProduto = gerais.filter(d => d.area === "produto");
  const porPeriodo = useMemo(() => {
    const map: Record<string, typeof filtradas> = {};
    for (const d of filtradas.filter(x => x.escopo === "periodo")) {
      const k = d.periodo ?? "—";
      (map[k] ??= []).push(d);
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtradas]);

  function Card({ d }: { d: typeof filtradas[number] }) {
    const ai = AREA_INFO[d.area] ?? AREA_INFO.redacao;
    return (
      <div className="rounded-xl border border-[var(--solar-line)] bg-white p-4 shadow-[var(--elev-1)]">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-semibold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{d.titulo}</p>
          <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: ai.bg, color: ai.color }}>{ai.label}</span>
        </div>
        {d.conteudo && <p className="text-[13px] text-[var(--ink-2)] leading-relaxed mt-1.5 whitespace-pre-wrap">{d.conteudo}</p>}
        {d.fonte && <p className="text-[11px] text-[var(--solar-mute)] mt-2">{d.fonte}</p>}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[var(--solar-bg)] overflow-hidden">
      <div className="bg-white border-b border-[var(--solar-line)] px-6 py-4 shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-lg text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>Direcionador</h1>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[var(--solar-ink)] text-white uppercase tracking-wider">Interno Raz</span>
            </div>
            <p className="text-[var(--solar-mute)] text-xs mt-0.5">Diretrizes de redação, arte e produto + apontamentos dos cards. Só o time Raz vê esta aba.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportarPromptClaude} disabled={exportando}
            title="Exporta todo o Direcionador como prompt pronto pra colar no Claude e usar de régua de validação"
            className="px-3 py-1.5 text-xs font-semibold rounded-[10px] text-white shadow-[var(--elev-1)] disabled:opacity-50"
            style={{ background: "var(--solar-ink)" }}>
            {exportando ? "Gerando…" : "⧉ Exportar pra Claude"}
          </button>
          <div className="flex items-center gap-1 bg-[var(--solar-bg)] rounded-lg p-1">
            <button onClick={() => setAba("diretrizes")} className="px-3 py-1.5 text-xs font-semibold rounded-md transition-colors" style={{ background: aba === "diretrizes" ? "#fff" : "transparent", color: aba === "diretrizes" ? "var(--solar-ink)" : "var(--solar-mute)" }}>Diretrizes</button>
            <button onClick={() => setAba("apontamentos")} className="px-3 py-1.5 text-xs font-semibold rounded-md transition-colors" style={{ background: aba === "apontamentos" ? "#fff" : "transparent", color: aba === "apontamentos" ? "var(--solar-ink)" : "var(--solar-mute)" }}>Apontamentos dos cards</button>
            <button onClick={() => setAba("validar")} className="px-3 py-1.5 text-xs font-semibold rounded-md transition-colors" style={{ background: aba === "validar" ? "#fff" : "transparent", color: aba === "validar" ? "var(--solar-ink)" : "var(--solar-mute)" }}>Validar Redação</button>
          </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {aba === "diretrizes" ? (
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex items-center gap-2">
              {(["todas", "redacao", "arte", "ambos", "produto"] as const).map(a => (
                <button key={a} onClick={() => setFiltroArea(a)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
                  style={{ background: filtroArea === a ? "var(--solar-ink)" : "#fff", color: filtroArea === a ? "#fff" : "var(--ink-2)", borderColor: filtroArea === a ? "var(--solar-ink)" : "var(--solar-line)" }}>
                  {a === "todas" ? "Todas" : AREA_INFO[a]?.label}
                </button>
              ))}
            </div>

            {isLoading ? (
              <p className="text-sm text-[var(--solar-mute)]">Carregando…</p>
            ) : (
              <>
                {(filtroArea === "todas" || filtroArea === "redacao" || filtroArea === "ambos") && geraisRedacao.length > 0 && (
                  <section>
                    <h2 className="text-[11px] font-bold text-[var(--solar-mute)] uppercase tracking-widest mb-3">Diretrizes gerais · Redação</h2>
                    <div className="grid gap-3">{geraisRedacao.map(d => <Card key={d.id} d={d} />)}</div>
                  </section>
                )}
                {(filtroArea === "todas" || filtroArea === "arte" || filtroArea === "ambos") && geraisArte.length > 0 && (
                  <section>
                    <h2 className="text-[11px] font-bold text-[var(--solar-mute)] uppercase tracking-widest mb-3">Diretrizes gerais · Arte</h2>
                    <div className="grid gap-3">{geraisArte.map(d => <Card key={d.id} d={d} />)}</div>
                  </section>
                )}
                {(filtroArea === "todas" || filtroArea === "produto") && geraisProduto.length > 0 && (
                  <section>
                    <h2 className="text-[11px] font-bold text-[var(--solar-mute)] uppercase tracking-widest mb-3">Diretrizes gerais · Produto</h2>
                    <p className="text-xs text-[var(--solar-mute)] -mt-2 mb-3">O que a plataforma faz, o que não faz e o que não pode ser prometido. Fonte: emiteaí Product Overview.</p>
                    <div className="grid gap-3">{geraisProduto.map(d => <Card key={d.id} d={d} />)}</div>
                  </section>
                )}
                {porPeriodo.length > 0 && (
                  <section>
                    <h2 className="text-[11px] font-bold text-[var(--solar-mute)] uppercase tracking-widest mb-3">Ajustes por período</h2>
                    <div className="space-y-6">
                      {porPeriodo.map(([periodo, itens]) => (
                        <div key={periodo}>
                          <p className="text-xs font-bold text-[var(--solar-mute)] mb-2">{mesLabel(periodo)}</p>
                          <div className="grid gap-3">{itens.map(d => <Card key={d.id} d={d} />)}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                {filtradas.length === 0 && <p className="text-sm text-[var(--solar-mute)]">Nenhuma diretriz cadastrada ainda para este cliente.</p>}
              </>
            )}
          </div>
        ) : aba === "apontamentos" ? (
          <div className="max-w-4xl mx-auto">
            <p className="text-xs text-[var(--solar-mute)] mb-4">Todos os comentários feitos nos cards deste cliente, mais recentes primeiro. Alimenta o direcionador automaticamente.</p>
            <div className="space-y-2.5">
              {apontamentos.map(a => (
                <div key={a.id} className="rounded-xl border border-[var(--solar-line)] bg-white p-3.5 shadow-[var(--elev-1)]">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-semibold text-[var(--solar-ink)]">{a.autor}</span>
                    <span className="text-[11px] text-[var(--solar-mute)]">{new Date(a.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                    {a.resolvido && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#D1FAE5] text-[#059669]">resolvido</span>}
                  </div>
                  <p className="text-[13px] text-[var(--ink-2)] leading-relaxed whitespace-pre-wrap">{a.texto}</p>
                  <p className="text-[11px] text-[var(--solar-mute)] mt-1.5">em <span className="font-medium text-[var(--solar-mute)]">{a.pautaTitulo}</span></p>
                </div>
              ))}
              {apontamentos.length === 0 && <p className="text-sm text-[var(--solar-mute)]">Nenhum comentário nos cards ainda.</p>}
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-4">
            <p className="text-xs text-[var(--solar-mute)]">Cole a redação, suba um .txt/.md ou puxe de um link do Google Doc. A IA valida cada post contra o Direcionador, português, consistência de dados, tom anti-IA e fit de produto. Roda por partes (com progresso), você marca o que já ajustou e o envio fica guardado no histórico.</p>

            <div className="rounded-xl border border-[var(--solar-line)] bg-white p-4 shadow-[var(--elev-1)] space-y-3">
              <div className="flex items-center gap-2">
                <input
                  value={valLink}
                  onChange={e => setValLink(e.target.value)}
                  placeholder="Link do Google Doc (precisa estar acessível por link)"
                  className="flex-1 rounded-lg border border-[var(--solar-line)] px-3 py-2 text-sm focus:border-[var(--brand)] focus:outline-none"
                />
                <button onClick={buscarLink} disabled={fetchDoc.isPending || !valLink.trim()}
                  className="rounded-lg border border-[var(--solar-line)] px-3 py-2 text-xs font-semibold text-[var(--ink-2)] hover:bg-[var(--solar-bg)] disabled:opacity-40">
                  {fetchDoc.isPending ? "Buscando…" : "Puxar doc"}
                </button>
              </div>
              <textarea
                value={valTexto}
                onChange={e => setValTexto(e.target.value)}
                rows={9}
                placeholder="…ou cole aqui o texto das pautas (POST 1…, POST 2…)."
                className="w-full rounded-lg border border-[var(--solar-line)] px-3 py-2 text-sm text-[var(--solar-ink)] focus:border-[var(--brand)] focus:outline-none font-mono"
              />
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-xs font-semibold text-[var(--ink-2)] cursor-pointer rounded-lg border border-[var(--solar-line)] px-3 py-2 hover:bg-[var(--solar-bg)]">
                  📎 Subir .txt/.md
                  <input type="file" accept=".txt,.md,.markdown,text/plain" className="hidden" onChange={onUploadArquivo} />
                </label>
                {valArquivo && <span className="text-[11px] text-[var(--solar-mute)]">{valArquivo}</span>}
                <span className="text-[11px] text-[var(--solar-mute)]">{valTexto.length.toLocaleString("pt-BR")} caracteres · {splitPosts(valTexto).length} post(s)</span>
                <button
                  onClick={rodarValidacao}
                  disabled={validando || valTexto.trim().length < 20}
                  className="ml-auto rounded-[10px] bg-[var(--solar-ink)] px-4 py-2 text-sm font-semibold text-white shadow-[var(--elev-1)] disabled:opacity-40"
                >
                  {validando ? "Validando…" : "Validar redação"}
                </button>
              </div>
            </div>

            {valProg && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2 text-[11px] text-[var(--solar-mute)] flex-wrap">
                  <span>{valEstado === "paused" ? "Pausado" : valEstado === "running" ? "Validando post a post…" : "Concluído"} {valProg.done}/{valProg.total}</span>
                  <div className="flex items-center gap-1.5">
                    {valEstado === "running" && (
                      <button onClick={() => { ctrlRef.current = "pause"; }} className="rounded-lg border border-[var(--solar-line)] px-2.5 py-1 font-semibold text-[var(--ink-2)] hover:bg-[var(--solar-bg)]">⏸ Pausar</button>
                    )}
                    {valEstado === "paused" && (
                      <button onClick={() => { ctrlRef.current = "run"; setValEstado("running"); }} className="rounded-lg border border-[#059669] px-2.5 py-1 font-semibold text-[#059669] hover:bg-[#ECFDF5]">▶ Continuar</button>
                    )}
                    {validando && (
                      <button onClick={() => { ctrlRef.current = "cancel"; }} className="rounded-lg border border-red-200 px-2.5 py-1 font-semibold text-red-600 hover:bg-red-50">✕ Cancelar</button>
                    )}
                    {!validando && valPosts.length > 0 && (
                      <button onClick={baixarApontamentos} className="rounded-lg border border-[var(--solar-ink)] px-2.5 py-1 font-semibold text-[var(--solar-ink)] hover:bg-[var(--solar-bg)]">⬇ Baixar (.md)</button>
                    )}
                  </div>
                </div>
                <div className="h-2 w-full rounded-full bg-[var(--solar-line)] overflow-hidden">
                  <div className="h-full bg-[var(--solar-ink)] transition-all" style={{ width: `${Math.round((valProg.done / valProg.total) * 100)}%` }} />
                </div>
                {valResumo && <p className="text-xs text-[var(--solar-mute)] pt-1">{valResumo}</p>}
              </div>
            )}

            {valPosts.map((p: any) => {
              const vinfo: Record<string, { bg: string; color: string; label: string }> = {
                aprovado:  { bg: "#D1FAE5", color: "#059669", label: "✅ Aprovado" },
                ajustes:   { bg: "#FEF3C7", color: "#B45309", label: "🟡 Ajustes" },
                reprovado: { bg: "#FEE2E2", color: "#DC2626", label: "🔴 Reprovado" },
              };
              const vi = vinfo[p.veredito] ?? vinfo.ajustes;
              const pend = (p.issues ?? []).filter((i: any) => !i.resolvido).length;
              return (
                <div key={p.id} className="rounded-xl border border-[var(--solar-line)] bg-white p-4 shadow-[var(--elev-1)]">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="text-sm font-semibold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{p.titulo}</p>
                    <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: vi.bg, color: vi.color }}>{pend === 0 && p.issues?.length ? "✅ Resolvido" : vi.label}</span>
                  </div>
                  {(!p.issues || p.issues.length === 0) ? (
                    <p className="text-[13px] text-[var(--solar-mute)]">Sem apontamentos.</p>
                  ) : (
                    <ul className="space-y-2">
                      {p.issues.map((i: any) => (
                        <li key={i.id} className="flex items-start gap-2 text-[13px] leading-relaxed">
                          <input type="checkbox" checked={!!i.resolvido} onChange={e => toggleItem(p.id, i.id, e.target.checked)} className="mt-0.5 shrink-0 accent-[#059669] cursor-pointer" />
                          <div className={i.resolvido ? "opacity-50 line-through" : ""}>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded mr-1.5" style={{ background: i.severidade === "erro" ? "#FEE2E2" : "#FEF3C7", color: i.severidade === "erro" ? "#DC2626" : "#B45309" }}>{i.categoria}</span>
                            <span className="text-[var(--ink-2)]">{i.problema}</span>
                            {i.trecho && <span className="block text-[var(--solar-mute)] text-xs mt-0.5">"{i.trecho}"</span>}
                            {i.sugestao && <span className="block text-[#059669] text-xs mt-0.5">→ {i.sugestao}</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}

            {valHistorico.length > 0 && (
              <div className="pt-2">
                <h2 className="text-[11px] font-bold text-[var(--solar-mute)] uppercase tracking-widest mb-2">Envios guardados</h2>
                <div className="space-y-1.5">
                  {valHistorico.map(h => (
                    <button key={h.id} onClick={() => abrirEnvio(h.id)} className="w-full text-left rounded-lg border border-[var(--solar-line)] bg-white px-3 py-2 hover:bg-[var(--solar-bg)]">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] text-[var(--ink-2)] truncate">{h.fonte || "colado"} · {h.totalPosts} post(s)</span>
                        <span className="text-[11px] text-[var(--solar-mute)] shrink-0">{new Date(h.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} · {h.criadoPor}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
